import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, User, Users, Network } from "lucide-react";
import { cn } from "@/lib/utils";

interface Employee {
  id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  position?: string;
  department?: string;
  manager_id?: string | null;
  invitation_status: string;
  accepted_at: string | null;
}

interface OrgChartProps {
  employees: Employee[];
}

interface EmployeeNodeProps {
  employee: Employee;
  employees: Employee[];
  level: number;
  isLast: boolean;
}

const EmployeeNode = ({ employee, employees, level, isLast }: EmployeeNodeProps) => {
  const [isExpanded, setIsExpanded] = useState(true);
  
  const directReports = employees.filter(e => e.manager_id === employee.id);
  const hasReports = directReports.length > 0;
  
  const displayName = employee.first_name && employee.last_name 
    ? `${employee.first_name} ${employee.last_name}`
    : employee.email || 'Sans nom';
  
  const isActive = employee.accepted_at !== null;

  return (
    <div className="relative">
      {/* Connector line from parent */}
      {level > 0 && (
        <div 
          className="absolute left-0 top-0 h-6 w-6 border-l-2 border-b-2 border-border rounded-bl-lg"
          style={{ marginLeft: '-12px' }}
        />
      )}
      
      <div className={cn("flex items-start gap-2", level > 0 && "ml-6")}>
        {/* Expand/Collapse button */}
        {hasReports ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 shrink-0"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        ) : (
          <div className="w-8 h-8 flex items-center justify-center shrink-0">
            <User className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
        
        {/* Employee card */}
        <div 
          className={cn(
            "flex-1 p-3 rounded-lg border transition-colors",
            hasReports 
              ? "bg-primary/5 border-primary/20 hover:border-primary/40" 
              : "bg-card hover:bg-muted/50",
            !isActive && "opacity-60"
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium truncate">{displayName}</span>
                {hasReports && (
                  <Badge variant="secondary" className="text-xs shrink-0">
                    <Users className="h-3 w-3 mr-1" />
                    {directReports.length}
                  </Badge>
                )}
              </div>
              {(employee.position || employee.department) && (
                <p className="text-sm text-muted-foreground truncate">
                  {employee.position}{employee.position && employee.department && ' • '}{employee.department}
                </p>
              )}
            </div>
            <Badge 
              variant={isActive ? "default" : "outline"}
              className={cn(
                "shrink-0 text-xs",
                isActive && "bg-success text-success-foreground"
              )}
            >
              {isActive ? "Actif" : "En attente"}
            </Badge>
          </div>
        </div>
      </div>
      
      {/* Direct reports */}
      {hasReports && isExpanded && (
        <div className={cn("mt-2 space-y-2 relative", level > 0 ? "ml-6" : "ml-4")}>
          {/* Vertical connector line */}
          <div 
            className="absolute left-4 top-0 bottom-4 border-l-2 border-border"
            style={{ marginLeft: '-12px' }}
          />
          
          {directReports.map((report, index) => (
            <EmployeeNode
              key={report.id}
              employee={report}
              employees={employees}
              level={level + 1}
              isLast={index === directReports.length - 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const OrgChart = ({ employees }: OrgChartProps) => {
  // Find root employees (those without a manager or whose manager is not in the list)
  const employeeIds = new Set(employees.map(e => e.id));
  const rootEmployees = employees.filter(
    e => !e.manager_id || !employeeIds.has(e.manager_id)
  );
  
  // Sort root employees: those with reports first, then by name
  const sortedRoots = [...rootEmployees].sort((a, b) => {
    const aReports = employees.filter(e => e.manager_id === a.id).length;
    const bReports = employees.filter(e => e.manager_id === b.id).length;
    if (bReports !== aReports) return bReports - aReports;
    const aName = `${a.first_name} ${a.last_name}`;
    const bName = `${b.first_name} ${b.last_name}`;
    return aName.localeCompare(bName);
  });

  // Calculate stats
  const managersCount = employees.filter(
    e => employees.some(emp => emp.manager_id === e.id)
  ).length;
  const unassignedCount = rootEmployees.filter(
    e => !employees.some(emp => emp.manager_id === e.id)
  ).length;

  if (employees.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">
            <Network className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Aucun employé pour afficher l'organigramme</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Network className="h-5 w-5" />
              Organigramme
            </CardTitle>
            <CardDescription>
              Visualisez la structure hiérarchique de votre équipe
            </CardDescription>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="text-center">
              <div className="font-semibold text-lg">{managersCount}</div>
              <div className="text-muted-foreground">Managers</div>
            </div>
            <div className="text-center">
              <div className="font-semibold text-lg">{unassignedCount}</div>
              <div className="text-muted-foreground">Sans équipe</div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {sortedRoots.map((employee, index) => (
            <EmployeeNode
              key={employee.id}
              employee={employee}
              employees={employees}
              level={0}
              isLast={index === sortedRoots.length - 1}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};